<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    >
      <template #label>
        <slot name="label" />
      </template>
    </AM3InputLabel>
    <div
      class="am3-input-wrapper flex h-11 flex-row items-center justify-between rounded-md"
      :class="[
        disabled ? 'disabled' : '',
        ($slots.right || $slots.left) ? 'focus-within:outline-brand hover:border-brand bg-surface-light px-3' : '',
        (disabled) ? disabledClasses : 'bg-surface-light hover:border-brand ',
        (validationState === false || typeof validationState === 'string') ? 'error border border-solid border-[var(--font-colour-warning)]' : buttonBorderClasses,
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-wrapper')"
    >
      <!-- Left slot -->
      <div
        v-if="$slots.left"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="left" />
      </div>

      <!-- Input element -->
      <input
        :id="inputId"
        ref="inputRef"
        :class="($slots.right || $slots.left) ? '' : 'focus:outline-brand focus:outline-offset-0' "
        class="am3-input bg-transparent px-3 py-2.5 text-sm h-11 w-full border-none outline-none  focus:ring-0 rounded-md relative"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :type="password ? 'password' : 'text'"
        :data-test-id="generateDataTestId(dataTestId, 'input')"
        :data-1p-ignore="disableAutocomplete ? '' : undefined"
        :autofocus
        :aria-label="ariaLabel || label"
        @input="updateValue"
        @change="$emit('change', $event.target.value.trim())"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        @focus="handleFocus"
        @blur="handleBlur"
      >

      <!-- Right slot -->
      <div
        v-if="$slots.right"
        class="am3-input-slot flex items-center justify-center"
      >
        <slot name="right" />
      </div>
    </div>

    <AM3InputError
      :error="validationState"
      :data-test-id="generateDataTestId(dataTestId, 'input-error')"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'

/**
 * AM3InputText component
 *
 * A flexible text input component with validation, restrictions, and slots
 */
export default defineComponent({
  name: 'AM3InputText',
  components: { AM3InputError, AM3InputLabel },

  props: {
    // v-model value
    modelValue: {
      type: [String, Number],
      default: '',
    },

    // Input label (string or false to hide)
    label: {
      type: [String, Boolean] as PropType<string | false>,
      default: '',
    },

    // Placeholder text
    placeholder: {
      type: String,
      default: props => 'Enter ' + props.label.toLowerCase(),
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required field
    required: {
      type: Boolean,
      default: false,
    },

    // Required field
    password: {
      type: Boolean,
      default: false,
    },

    // Validation function
    validate: {
      type: Function as PropType<(value: string) => boolean | string>,
      default: () => true,
    },

    // Validate on blur
    validateOnBlur: {
      type: Boolean,
      default: false,
    },

    // add the autofocus attr - for use in collapsible dialogs and similar
    autofocus: {
      type: Boolean,
      default: false,
    },

    // Input restriction function
    restrict: {
      type: Function as PropType<(event: Event) => boolean>,
      default: () => true,
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.label || 'text-input',
    },

    // Disable autocomplete
    disableAutocomplete: {
      type: Boolean,
      default: false,
    },

    // Aria label for accessibility
    ariaLabel: {
      type: String,
      required: false,
    },
  },

  emits: ['update:modelValue', 'change', 'enter', 'focus', 'blur'],

  setup(props, { emit }) {
    const buttonBorderClasses = ' rounded-md border border-1 border-surface-dark outline-1 outline-offset-0 outline-transparent outline-none'
    const disabledClasses = ' text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none'

    // Generate unique ID for the input
    const inputId = ref(`am3-input-${uuidv4()}`)
    const inputRef = ref<HTMLInputElement | null>(null)

    // Setup form validation
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
      validateOnBlur,
      handleBlur: _handleBlur,
    } = useFormValidation(props.validate, props.modelValue, props.required)

    // Sync the validateOnBlur prop with the composable ref
    validateOnBlur.value = props.validateOnBlur

    // Handle focus with custom logic
    const handleFocus = () => {
      emit('focus')
    }

    // Handle blur with custom logic
    const handleBlur = () => {
      _handleBlur(props.modelValue)
      emit('blur')
    }

    // Public method to run validation
    const runValidation = (value = props.modelValue, checkRequired = props.required) => {
      return _runValidation(value, checkRequired)
    }

    // Update model value
    const updateValue = (event: Event) => {
      const target = event.target as HTMLInputElement
      emit('update:modelValue', target.value)
    }

    // Handle keydown event with restrictions
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!props.restrict(event)) {
        event.preventDefault()
      }

      if (event.key === 'Enter') {
        emit('enter', (event.target as HTMLInputElement).value)
      }
    }

    // Handle paste event with restrictions
    const handlePaste = (event: ClipboardEvent) => {
      if (!props.restrict(event)) {
        event.preventDefault()
      }
    }

    return {
      inputId,
      inputRef,
      validationState,
      updateValue,
      handleKeyDown,
      handlePaste,
      buttonBorderClasses,
      disabledClasses,
      generateDataTestId,
      handleFocus,
      handleBlur,
      runValidation,
      resetValidation,
    }
  },
})
</script>
